import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { appsApi } from "@/api/apps";
import { queryKeys } from "@/lib/query-keys";
import {
  ArrowLeft,
  Bot,
  Globe,
  Lock,
  Play,
  Square,
  Trash2,
  ExternalLink,
  Heart,
  RefreshCw,
  RotateCcw,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronRight,
  Settings,
  Cloud,
  Rocket,
  Shield,
  Eye,
  EyeOff,
  Plus,
  X,
} from "lucide-react";

const statusVariant: Record<
  string,
  "success" | "warning" | "danger" | "default" | "secondary"
> = {
  deployed: "success",
  building: "warning",
  draft: "secondary",
  failed: "danger",
  stopped: "default",
};

const healthVariant: Record<
  string,
  "success" | "warning" | "danger" | "default"
> = {
  healthy: "success",
  unhealthy: "danger",
  unreachable: "warning",
};

export function AppDetail({
  appId: embeddedAppId,
  onBack,
}: {
  appId?: string;
  onBack?: () => void;
} = {}) {
  const { appId: routeAppId } = useParams<{ appId: string }>();
  const appId = embeddedAppId ?? routeAppId;
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [iframeExpanded, setIframeExpanded] = useState(false);
  const [showHandoverForm, setShowHandoverForm] = useState(false);
  const [handoverNotes, setHandoverNotes] = useState("");
  const [showHandoverNotes, setShowHandoverNotes] = useState(false);
  const [envEditMode, setEnvEditMode] = useState(false);
  const [envDraft, setEnvDraft] = useState<{ key: string; value: string }[]>([]);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const { data: allAgents = [] } = useAgentsByCompany(tenantId);

  const { data: app, isLoading } = useQuery({
    queryKey: queryKeys.apps.detail(appId!),
    queryFn: () => appsApi.get(appId!),
    enabled: !!appId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "building" ? 5000 : false;
    },
  });

  const { data: deployments = [] } = useQuery({
    queryKey: queryKeys.apps.deployments(appId!),
    queryFn: () => appsApi.listDeployments(appId!),
    enabled: !!appId,
  });

  const toggleVisibility = useMutation({
    mutationFn: (vis: "agents-only" | "internal" | "public") =>
      appsApi.toggleVisibility(appId!, vis),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => appsApi.stop(appId!),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      }),
  });

  const startMutation = useMutation({
    mutationFn: () => appsApi.start(appId!),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      }),
  });

  const healthMutation = useMutation({
    mutationFn: () => appsApi.checkHealth(appId!),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      }),
  });

  const restartMutation = useMutation({
    mutationFn: () => appsApi.restart(appId!),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      }),
  });

  const promoteMutation = useMutation({
    mutationFn: () => appsApi.promote(appId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.deployments(appId!) });
    },
  });

  const registerMcpMutation = useMutation({
    mutationFn: () => appsApi.registerMcp(appId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
    },
  });

  const assignMcpMutation = useMutation({
    mutationFn: (agentId: string) => appsApi.assignMcp(appId!, { agentIds: [agentId] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
    },
  });

  const handoverMutation = useMutation({
    mutationFn: (notes?: string) => appsApi.handover(appId!, notes),
    onSuccess: () => {
      setShowHandoverForm(false);
      setHandoverNotes("");
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(appId!),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => appsApi.delete(appId!),
    onSuccess: () => {
      if (onBack) {
        onBack();
        return;
      }
      navigate(`/tenants/${tenantId}/apps`);
    },
  });

  const isDeployedOrStopped =
    app?.status === "deployed" || app?.status === "stopped";

  const { data: envVars = {} } = useQuery({
    queryKey: ["app-env", appId],
    queryFn: () => appsApi.getEnvVars(appId!),
    enabled: !!appId && isDeployedOrStopped,
  });

  const updateEnvMutation = useMutation({
    mutationFn: (vars: Record<string, string>) =>
      appsApi.updateEnvVars(appId!, vars),
    onSuccess: () => {
      setEnvEditMode(false);
      queryClient.invalidateQueries({ queryKey: ["app-env", appId] });
    },
  });

  if (isLoading) {
    return (
      <CenterPanel>
        <p className="text-sm text-text-muted">Loading app...</p>
      </CenterPanel>
    );
  }

  if (!app) {
    return (
      <CenterPanel>
        <p className="text-sm text-text-muted">App not found</p>
      </CenterPanel>
    );
  }

  const isDeployed = !!app.appUrl && app.status === "deployed";
  const appMcp = app.metadata?.appMcp && typeof app.metadata.appMcp === "object"
    ? app.metadata.appMcp as { tenantMcpId?: string; url?: string; assignedAgentIds?: string[] }
    : null;

  return (
    <CenterPanel>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => {
            if (onBack) {
              onBack();
              return;
            }
            navigate(`/tenants/${tenantId}/apps`);
          }}
          className="mb-3 flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Apps
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              {app.name}
            </h1>
            {app.description && (
              <p className="mt-1 text-sm text-text-secondary">
                {app.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant[app.status] ?? "default"}>
              {app.status}
            </Badge>
            {(app.buildPhase === "handed_over" || app.handedOverAt) && (
              <Badge variant="success">Handed Over</Badge>
            )}
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              {app.visibility === "public" ? (
                <Globe size={14} />
              ) : app.visibility === "agents-only" ? (
                <Bot size={14} />
              ) : (
                <Lock size={14} />
              )}
              {app.visibility}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mb-6 flex flex-wrap gap-2">
        {isDeployed && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(app.appUrl!, "_blank")}
            >
              <ExternalLink size={14} className="mr-1.5" />
              Open in browser
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => healthMutation.mutate()}
              disabled={healthMutation.isPending}
            >
              <Heart size={14} className="mr-1.5" />
              {healthMutation.isPending ? "Checking..." : "Health check"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
            >
              <Square size={14} className="mr-1.5" />
              Stop
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => restartMutation.mutate()}
              disabled={restartMutation.isPending}
            >
              <RotateCcw size={14} className="mr-1.5" />
              {restartMutation.isPending ? "Restarting..." : "Restart"}
            </Button>
          </>
        )}
        {app.status === "stopped" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
          >
            <Play size={14} className="mr-1.5" />
            Start
          </Button>
        )}
        {app.builtByAgentId && (
          <Button
            size="sm"
            onClick={() => {
              if (confirm("Promote the current App Builder dev snapshot to the hosted app?")) {
                promoteMutation.mutate();
              }
            }}
            disabled={promoteMutation.isPending}
          >
            <Rocket size={14} className="mr-1.5" />
            {promoteMutation.isPending ? "Promoting..." : app.appUrl ? "Promote Update" : "Host App"}
          </Button>
        )}
        {app.status === "deployed" && app.buildPhase !== "handed_over" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowHandoverForm(true)}
          >
            Hand Over
          </Button>
        )}
        <Button
          size="sm"
          variant="destructive"
          onClick={() => {
            if (confirm(`Delete app "${app.name}"? This will also remove Azure resources.`)) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
        >
          <Trash2 size={14} className="mr-1.5" />
          Delete
        </Button>
      </div>

      {/* Handover form */}
      {showHandoverForm && (
        <Card className="mb-6">
          <CardContent className="py-4">
            <p className="mb-2 text-sm font-medium text-text-primary">
              Hand over this app
            </p>
            <textarea
              className="mb-3 w-full rounded-md border border-border bg-surface-primary p-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
              rows={3}
              placeholder="Optional handover notes..."
              value={handoverNotes}
              onChange={(e) => setHandoverNotes(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  handoverMutation.mutate(
                    handoverNotes.trim() || undefined
                  )
                }
                disabled={handoverMutation.isPending}
              >
                {handoverMutation.isPending
                  ? "Handing over..."
                  : "Confirm Handover"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowHandoverForm(false);
                  setHandoverNotes("");
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Handover notes (collapsible) */}
      {app.handoverNotes && (
        <Card className="mb-6">
          <CardContent className="py-3">
            <button
              className="flex w-full items-center gap-1.5 text-sm font-medium text-text-primary"
              onClick={() => setShowHandoverNotes(!showHandoverNotes)}
            >
              {showHandoverNotes ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              Handover Notes
            </button>
            {showHandoverNotes && (
              <p className="mt-2 text-sm text-text-secondary whitespace-pre-wrap">
                {app.handoverNotes}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Visibility selector */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <p className="mb-3 text-xs font-medium text-text-muted uppercase tracking-wide">
            Visibility
          </p>
          <div className="flex flex-col gap-2">
            {([
              {
                value: "agents-only" as const,
                label: "Agents Only",
                desc: "VNet only. Agent API access via API keys.",
                icon: <Bot size={14} />,
              },
              {
                value: "internal" as const,
                label: "Internal",
                desc: "VNet + platform. Accessible to team via ProClaw.",
                icon: <Lock size={14} />,
              },
              {
                value: "public" as const,
                label: "Public",
                desc: "Internet-facing. Requires admin approval. Entra ID auth enabled.",
                icon: <Globe size={14} />,
              },
            ]).map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                  app.visibility === opt.value
                    ? "border-accent bg-accent/5"
                    : "border-border hover:border-text-muted"
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={opt.value}
                  checked={app.visibility === opt.value}
                  onChange={() => toggleVisibility.mutate(opt.value)}
                  disabled={toggleVisibility.isPending}
                  className="mt-0.5"
                />
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 text-text-muted">{opt.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {opt.label}
                    </p>
                    <p className="text-xs text-text-muted">{opt.desc}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* App preview (iframe) */}
      {isDeployed && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm">App Preview</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.apps.detail(appId!),
                  })
                }
              >
                <RefreshCw size={14} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIframeExpanded(!iframeExpanded)}
              >
                {iframeExpanded ? (
                  <Minimize2 size={14} />
                ) : (
                  <Maximize2 size={14} />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <iframe
              src={app.appUrl!}
              title={app.name}
              className={`w-full border-t border-border ${
                iframeExpanded ? "h-[80vh]" : "h-[500px]"
              }`}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </CardContent>
        </Card>
      )}

      {/* App details grid */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-text-muted">Framework</p>
            <p className="text-sm font-medium text-text-primary">
              {app.framework || "Not specified"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-text-muted">Version</p>
            <p className="text-sm font-medium text-text-primary">
              {app.currentVersion || "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-text-muted">Health</p>
            <div className="flex items-center gap-2">
              {app.lastHealthStatus ? (
                <Badge
                  variant={
                    healthVariant[app.lastHealthStatus] ?? "default"
                  }
                >
                  {app.lastHealthStatus}
                </Badge>
              ) : (
                <span className="text-sm text-text-muted">Not checked</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-text-muted">Azure Resource</p>
            <p className="text-sm font-medium text-text-primary truncate">
              {app.appServiceName || "Not provisioned"}
            </p>
          </CardContent>
        </Card>
        {app.appUrl && (
          <Card className="col-span-2">
            <CardContent className="py-4">
              <p className="text-xs text-text-muted">URL</p>
              <a
                href={app.appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-accent hover:underline"
              >
                {app.appUrl}
              </a>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Agent tool exposure */}
      {app.appUrl && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Bot size={16} />
              Agent Tools
            </CardTitle>
            <Button
              size="sm"
              variant={appMcp?.tenantMcpId ? "outline" : "default"}
              onClick={() => registerMcpMutation.mutate()}
              disabled={registerMcpMutation.isPending}
            >
              {registerMcpMutation.isPending
                ? "Registering..."
                : appMcp?.tenantMcpId
                  ? "Refresh MCP"
                  : "Register MCP"}
            </Button>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-text-secondary">
              {appMcp?.tenantMcpId
                ? "MCP endpoint: " + (appMcp.url || app.appUrl.replace(/\/$/, "") + "/mcp")
                : "Register this app as an MCP server so agents can use its tools."}
            </p>
            {appMcp?.tenantMcpId && (
              <div className="flex flex-col gap-2">
                {allAgents
                  .filter((agent) => agent.id !== app.builtByAgentId)
                  .map((agent) => {
                    const assigned = appMcp.assignedAgentIds?.includes(agent.id);
                    return (
                      <div
                        key={agent.id}
                        className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                      >
                        <div>
                          <p className="text-sm font-medium text-text-primary">{agent.name}</p>
                          <p className="text-xs text-text-muted">{agent.role}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => assignMcpMutation.mutate(agent.id)}
                          disabled={assigned || assignMcpMutation.isPending}
                        >
                          {assigned ? "Added" : "Add MCP"}
                        </Button>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deployment history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Deployment History</CardTitle>
        </CardHeader>
        <CardContent>
          {deployments.length === 0 ? (
            <p className="text-sm text-text-muted">No deployments yet</p>
          ) : (
            <div className="flex flex-col gap-2">
              {deployments.map((dep) => (
                <div
                  key={dep.id}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        dep.status === "succeeded"
                          ? "success"
                          : dep.status === "failed"
                            ? "danger"
                            : dep.status === "deploying"
                              ? "warning"
                              : "default"
                      }
                    >
                      {dep.status}
                    </Badge>
                    <span className="text-sm text-text-primary">
                      {dep.version || "unversioned"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-text-muted">
                    {dep.buildDurationMs && (
                      <span>
                        {(dep.buildDurationMs / 1000).toFixed(1)}s
                      </span>
                    )}
                    <span>
                      {new Date(dep.startedAt).toLocaleString()}
                    </span>
                    <span>{dep.triggeredBy}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Environment Variables */}
      {isDeployedOrStopped && (
        <Card className="mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Settings size={16} />
              Environment Variables
            </CardTitle>
            {!envEditMode && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const entries = Object.entries(envVars);
                  setEnvDraft(
                    entries.length > 0
                      ? entries.map(([key, value]) => ({ key, value }))
                      : [{ key: "", value: "" }]
                  );
                  setEnvEditMode(true);
                }}
              >
                {Object.keys(envVars).length === 0 ? "Add" : "Edit"}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {envEditMode ? (
              <div className="flex flex-col gap-3">
                {envDraft.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      className="flex-1 rounded-md border border-border bg-surface-primary px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                      placeholder="KEY"
                      value={row.key}
                      onChange={(e) => {
                        const next = [...envDraft];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setEnvDraft(next);
                      }}
                    />
                    <input
                      className="flex-1 rounded-md border border-border bg-surface-primary px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                      placeholder="Value"
                      value={row.value}
                      onChange={(e) => {
                        const next = [...envDraft];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setEnvDraft(next);
                      }}
                    />
                    <button
                      className="p-1 text-text-muted hover:text-danger transition-colors"
                      onClick={() =>
                        setEnvDraft(envDraft.filter((_, i) => i !== idx))
                      }
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  className="self-start"
                  onClick={() =>
                    setEnvDraft([...envDraft, { key: "", value: "" }])
                  }
                >
                  <Plus size={14} className="mr-1.5" />
                  Add Variable
                </Button>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      const vars: Record<string, string> = {};
                      // Include all original keys so we can detect deletions
                      for (const key of Object.keys(envVars)) {
                        vars[key] = "";
                      }
                      // Overwrite with draft values
                      for (const { key, value } of envDraft) {
                        if (key.trim()) {
                          vars[key.trim()] = value;
                        }
                      }
                      updateEnvMutation.mutate(vars);
                    }}
                    disabled={updateEnvMutation.isPending}
                  >
                    {updateEnvMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEnvEditMode(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : Object.keys(envVars).length === 0 ? (
              <p className="text-sm text-text-muted">
                No environment variables configured
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {Object.entries(envVars).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                  >
                    <span className="text-sm font-mono font-medium text-text-primary">
                      {key}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-text-secondary">
                        {revealedKeys.has(key) ? value : "\u2022\u2022\u2022\u2022\u2022\u2022"}
                      </span>
                      <button
                        className="p-1 text-text-muted hover:text-text-primary transition-colors"
                        onClick={() => {
                          const next = new Set(revealedKeys);
                          if (next.has(key)) {
                            next.delete(key);
                          } else {
                            next.add(key);
                          }
                          setRevealedKeys(next);
                        }}
                      >
                        {revealedKeys.has(key) ? (
                          <EyeOff size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Azure Resources */}
      {isDeployedOrStopped && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Cloud size={16} />
              Azure Resources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-text-muted">App Service</p>
                <p className="font-medium text-text-primary">
                  {app.appServiceName || "\u2014"}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Resource Group</p>
                <p className="font-medium text-text-primary">
                  {app.resourceGroup || "\u2014"}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted">URL</p>
                {app.appUrl ? (
                  <a
                    href={app.appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-accent hover:underline"
                  >
                    {app.appUrl}
                  </a>
                ) : (
                  <p className="font-medium text-text-primary">{"\u2014"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-text-muted">Custom Domain</p>
                <p className="font-medium text-text-primary">
                  {app.customDomain || "None"}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Easy Auth</p>
                <Badge variant={app.easyAuthEnabled ? "success" : "default"}>
                  {app.easyAuthEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-text-muted">Visibility</p>
                <div className="flex items-center gap-1.5 text-text-primary">
                  {app.visibility === "public" ? (
                    <Globe size={14} />
                  ) : app.visibility === "agents-only" ? (
                    <Bot size={14} />
                  ) : (
                    <Lock size={14} />
                  )}
                  <span className="font-medium">{app.visibility}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {app.azureResourceId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      `https://portal.azure.com/#@/resource${app.azureResourceId}`,
                      "_blank"
                    )
                  }
                >
                  <ExternalLink size={14} className="mr-1.5" />
                  Azure Portal
                </Button>
              )}
              {app.appServiceName && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      `https://${app.appServiceName}.scm.azurewebsites.net`,
                      "_blank"
                    )
                  }
                >
                  <ExternalLink size={14} className="mr-1.5" />
                  Kudu Console
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domain & SSL */}
      {isDeployedOrStopped && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Shield size={16} />
              Domain & SSL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <p className="text-xs text-text-muted">Default Domain</p>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-primary">
                    {app.appServiceName
                      ? `${app.appServiceName}.azurewebsites.net`
                      : "\u2014"}
                  </span>
                  {app.appServiceName && (
                    <Badge variant="success">HTTPS enforced</Badge>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-text-muted">Custom Domain</p>
                <p className="font-medium text-text-primary">
                  {app.customDomain || "No custom domain configured"}
                </p>
              </div>
              <p className="text-xs text-text-muted">
                Custom domains can be added via the{" "}
                {app.azureResourceId ? (
                  <a
                    href={`https://portal.azure.com/#@/resource${app.azureResourceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Azure Portal
                  </a>
                ) : (
                  "Azure Portal"
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </CenterPanel>
  );
}
